/**
 * Single source of truth for the light/dark theme.
 *
 * Previously the theme was only ever toggled inside the signed-in settings
 * pages and was never applied on boot, so anonymous visitors had no way to
 * switch and a saved preference didn't survive a reload. This module centralises
 * read/apply/persist + a tiny pub-sub so any component (incl. a public navbar
 * toggle) can drive the theme, and `initTheme()` applies it at startup.
 *
 * The no-flash boot apply lives as an inline script in index.html; this module
 * re-applies on mount (idempotent) and wires the "system" OS listener.
 */

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'safaqat24.display.theme';
// Legacy key written by the old standalone settings page; read for back-compat.
const LEGACY_KEY = 'theme';

const listeners = new Set<(mode: ThemeMode) => void>();

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function getStoredThemeMode(): ThemeMode {
  try {
    const value = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (isThemeMode(value)) return value;
  } catch {
    /* localStorage unavailable (SSR / privacy mode) — fall through */
  }
  return 'system';
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Whether a given mode should render dark right now. */
export function resolveDark(mode: ThemeMode): boolean {
  return mode === 'dark' || (mode === 'system' && systemPrefersDark());
}

/** Toggle the `.dark` class on <html> for a mode. Does not persist. */
export function applyDomTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolveDark(mode));
}

/** Persist + apply + broadcast a theme mode. */
export function setThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
    localStorage.setItem(LEGACY_KEY, mode); // keep legacy readers in sync
  } catch {
    /* ignore persistence failures */
  }
  applyDomTheme(mode);
  listeners.forEach((cb) => cb(mode));
}

/** Apply the stored theme at boot and keep "system" in sync with the OS. */
export function initTheme(): void {
  applyDomTheme(getStoredThemeMode());
  if (typeof window !== 'undefined' && window.matchMedia) {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', () => {
        if (getStoredThemeMode() === 'system') applyDomTheme('system');
      });
  }
}

/** Subscribe to in-app theme changes. Returns an unsubscribe fn. */
export function subscribeTheme(cb: (mode: ThemeMode) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
